// @flow

import React, { useEffect, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import classNames from "classnames";
import { push } from "connected-react-router";
import { route } from "@routes/helper";
import { formatDateTime } from "@transformers/date";
import { formatAuthoredBy } from "@transformers/names";
import { Button, ButtonGroup, Pagination, Icons, View, GSGrid, Loader } from "@components";
import * as sharedSelectors from "@modules/shared/selectors";
import * as serviceSelectors from "@modules/service/selectors";
import { getClientSettings } from "@modules/settings/selectors";
import actionCreators from "@modules/service/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import type { AlertShort } from "@modules/shared/reducer/alerts/types";
import type { AbsenceMonitoring } from "@modules/service/reducer/absence-monitoring/types";
import type { ApprovableContainer } from "@modules/service/types";
import { AbsenceMonitoringPromotion, AbsenceMonitoringAddModal } from "@modules/service/components";
import type { State } from "@store/reducers";
import type { ClientSettingsSettingsType } from "@modules/settings/types";
import type { HierarchyAssignment } from "@modules/service/types";

type FilterState = null | boolean;

type ValueAndScore = {
  value: any,
  isScoring: boolean,
};

type ValuesAndScoreRecord = {
  levels: ValueAndScore,
  noActionList: ValueAndScore,
  supportList: ValueAndScore,
  kpiTarget: ValueAndScore,
  hierarchyAreas: ValueAndScore,
};

type PropsFromState = $Exact<{
  absenceMonitoringList: Array<ApprovableContainer<AbsenceMonitoring>>,
  activeMonitoringPromptAlerts: Array<AlertShort> | null,
  clientSettings: ?ClientSettingsSettingsType,
  isLoading: boolean,
  schema: ?Object,
  assignments: Array<HierarchyAssignment>,
}>;

type PropsFromDispatch = $Exact<{
  newAbsenceMonitoring: typeof actionCreators.NewAbsenceMonitoring,
  getAbsenceMonitoringList: typeof actionCreators.GetAbsenceMonitoringList,
  getReferralList: typeof actionCreators.GetReferralList,
  push: typeof push,
  getAlerts: typeof sharedActionCreators.GetAlerts,
  getSchema: typeof actionCreators.GetAbsenceMonitoringSchema,
  getHierarchyTree: typeof sharedActionCreators.GetHierarchyTree,
}>;

type Props = $Exact<{
  clientId: number,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  isEnabled: boolean,
}>;

export function AbsenceMonitoringTable(props: Props) {
  const { clientId } = props;
  const [filter, setFilter] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isEnabled = (props.clientSettings || {}).absenceMonitoring;

  const absenceMonitoringGridColumns = [
    {
      name: props.t("ID"),
      align: "center",
      width: 60,
      key: "id",
    },
    {
      name: props.t("ABSENCE_MONITORING_PROCESS_NAME"),
      key: "name",
      align: "left",
      renderCell: ({ row }: Object) => GSGrid.render.ellipsis(row.content.name),
    },

    {
      name: props.t("DATE_CREATED"),
      width: 140,
      renderCell: ({ row }: { row: Object }) => formatDateTime(row.createdAt),
    },
    {
      name: props.t("CREATED_BY"),
      width: "min-content",
      renderCell: ({ row }: { row: Object }) => formatAuthoredBy(row.createdBy),
      align: "left",
    },

    {
      key: "levels",
      name: props.t("LEVELS"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_MONITOR_TABLE_LEVELS"),
      },
      renderCell: ({ row }: { row: Object }) => {
        const levels = getValuesAndScore(row).levels;
        return getColorPillRender(levels.value, levels.isScoring);
      },
    },
    {
      key: "noActionList",
      name: props.t("NO_ACTION_LIST"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_MONITOR_TABLE_NO_ACTION_LIST"),
      },
      renderCell: ({ row }: { row: Object }) => {
        const noActionList = getValuesAndScore(row).noActionList;
        return getColorPillRender(noActionList.value, noActionList.isScoring);
      },
    },
    {
      key: "supportList",
      name: props.t("SUPPORT_LIST"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_MONITOR_TABLE_SUPPORT_LIST"),
      },
      renderCell: ({ row }: { row: Object }) => {
        const supportList = getValuesAndScore(row).supportList;
        return getColorPillRender(supportList.value, supportList.isScoring);
      },
    },
    {
      key: "kpiTarget",
      name: props.t("KPI_TARGET"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_MONITOR_TABLE_KPI_TARGET"),
      },
      renderCell: ({ row }: { row: Object }) => {
        const kpiTarget = getValuesAndScore(row).kpiTarget;
        return getColorPillRender(kpiTarget.value, kpiTarget.isScoring);
      },
    },
    {
      key: "hierarchyAreas",
      name: props.t("HIERARCHY_AREAS"),
      tooltip: {
        title: props.t("BEST_PRACTICE"),
        text: props.t("SERVICE_FEATURES_MONITOR_TABLE_HIERARCHY_AREAS"),
      },
      renderCell: ({ row }: { row: Object }) => {
        const hierarchyAreas = getValuesAndScore(row).hierarchyAreas;
        return getColorPillRender(hierarchyAreas.value, hierarchyAreas.isScoring);
      },
    },
    {
      key: "rating",
      name: props.t("RATING"),
      renderCell: ({ row }: { row: Object }) => {
        const valuesAndScore = Object.values(getValuesAndScore(row));

        if (!Array.isArray(valuesAndScore)) return;
        //$FlowIgnore
        const totalScore = valuesAndScore.filter(v => v.isScoring === true).length;
        return GSGrid.render.scorePill(totalScore);
      },
    },

    {
      name: props.t("STATUS"),
      width: 105,
      renderCell: ({ row }: Object) =>
        GSGrid.render.statusPill(props.t(row.content.isActive ? "ACTIVE" : "INACTIVE")),
    },
    {
      name: props.t("VIEW"),
      width: 50,
      onCellClick: row => absenceMonitoringHandleViewRevision(row.id),
      renderCell: ({ row }: { row: Object }) => GSGrid.render.materialIcon("search"),
    },
  ];

  useEffect(() => {
    if (!props.assignments.length) {
      props.getSchema({ clientId });
      props.getHierarchyTree({ clientId });
    }
  }, [props.assignments.length]);

  function getValuesAndScore(row: Object): ValuesAndScoreRecord {
    const levels = row.content.levels.map(level => level.isEnabled).length;
    const noActionList = row.content.noActionReasons.map(reason => reason.isActive).length;
    const supportList = row.content.additionalSupportActions.map(action => action.isActive).length;
    const kpiTarget = row.content.kpis.at(-1)?.kpiMeasure;
    const hierarchyAreas = row.hierarchyAssignments.length;

    return {
      levels: {
        value: levels,
        isScoring: levels >= 3,
      },
      noActionList: {
        value: noActionList,
        isScoring: noActionList > 1,
      },
      supportList: {
        value: supportList,
        isScoring: supportList > 1,
      },
      kpiTarget: {
        value: kpiTarget,
        isScoring: kpiTarget <= 168,
      },
      hierarchyAreas: {
        value: hierarchyAreas,
        isScoring: hierarchyAreas > 0,
      },
    };
  }

  function getColorPillRender(value: number | string | boolean, isGreen: boolean) {
    return GSGrid.render.colorPill(value, isGreen && "mint", isGreen && "mint-d2");
  }

  function toggleModal() {
    setIsModalOpen(prev => !prev);
  }

  const absenceMonitoringLiveData = props.absenceMonitoringList || [];

  useEffect(() => {
    isEnabled &&
      props.getAbsenceMonitoringList({
        clientId,
        isActive: filter,
      }) &&
      props.getAlerts({
        clientId,
      });
  }, [filter, clientId]);

  function absenceMonitoringHandleViewRevision(id: number) {
    props.push(
      route("ADMINISTRATION_SERVICE_ABSENCE_MONITORING_DETAILS", {
        clientId,
        processId: id,
      }),
    );
  }

  function absenceMonitoringHandleAddSetup() {
    props.newAbsenceMonitoring();
    toggleModal();
  }

  function absenceMonitoringHandleChangeFilter(filter: FilterState) {
    return function() {
      setFilter(filter);
    };
  }

  return (
    (isEnabled && (
      <View>
        {props.isLoading && <Loader inline />}
        <View className="message-box" margin={[0, 0, "1rem", 0]}>
          {props.t("ABSENCE_MONITORING_INFORMATION")}
        </View>
        <View className="gs-container">
          <View margin={[0, 0, "1rem", 0]} direction="row" justifyContent="space-between">
            <ButtonGroup
              config={[
                {
                  label: props.t("ALL"),
                  color: filter === null && "selected",
                  onClick: absenceMonitoringHandleChangeFilter(null),
                },
                {
                  label: props.t("ACTIVE"),
                  color: filter === true && "selected",
                  onClick: absenceMonitoringHandleChangeFilter(true),
                },
                {
                  label: props.t("INACTIVE"),
                  color: filter === false && "selected",
                  onClick: absenceMonitoringHandleChangeFilter(false),
                },
              ]}
            ></ButtonGroup>

            <Button
              materialIcon="add_box"
              onClick={absenceMonitoringHandleAddSetup}
              disabled={
                props.activeMonitoringPromptAlerts &&
                props.activeMonitoringPromptAlerts.length === 0
              }
            >
              {props.t("ADD")}
            </Button>
          </View>

          {props.activeMonitoringPromptAlerts && props.activeMonitoringPromptAlerts.length === 0 && (
            <View>
              <div className="alert alert-danger">
                {props.t("ABSENCE_MONITORING_NO_ALERT_CONFIGURATION")}
              </div>
            </View>
          )}

          <View>
            {absenceMonitoringLiveData.length ? (
              <GSGrid columns={absenceMonitoringGridColumns} rows={absenceMonitoringLiveData} />
            ) : (
              <h4 className="text-pinkish-grey text-center mt-5">
                {props.t("ABSENCE_MONITORING_LIVE_REASONS")}
              </h4>
            )}
          </View>
        </View>
        {isModalOpen && props.schema && (
          <AbsenceMonitoringAddModal
            clientId={clientId}
            editing={isModalOpen}
            schema={props.schema}
            isActive={filter}
            onCancel={toggleModal}
          />
        )}
      </View>
    )) || <AbsenceMonitoringPromotion />
  );
}

const mapStateToProps = (state: State): PropsFromState => ({
  absenceMonitoringList: serviceSelectors.getAbsenceMonitoringList(state),
  activeMonitoringPromptAlerts: sharedSelectors.getActiveMonitoringPromptAlerts(state),
  clientSettings: getClientSettings(state),
  isLoading: serviceSelectors.isLoading(state),
  schema: serviceSelectors.getAbsenceMonitoringSchema(state),
  assignments: serviceSelectors.getAbsenceMonitoringAssignments(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  push,
  newAbsenceMonitoring: actionCreators.NewAbsenceMonitoring,
  getReferralList: actionCreators.GetReferralList,
  getAbsenceMonitoringList: actionCreators.GetAbsenceMonitoringList,
  getAlerts: sharedActionCreators.GetAlerts,
  getSchema: actionCreators.GetAbsenceMonitoringSchema,
  getHierarchyTree: sharedActionCreators.GetHierarchyTree,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceMonitoringTable);
